/**
 * Help & learning (@digitalisation/enlixy-comp/onboarding), as in the other
 * Enlixy apps:
 * - CockpitTourProvider: the guided tours; each step brings the screen into
 *   place first (start page or a machine, the KI-Assistent drawer).
 * - Onboarding: binds the learning progress to the signed-in user, opens the
 *   welcome on the first visit, toasts learning-path milestones, F1 / "?"
 *   open the help, and mounts the sheet, the welcome and the shortcuts on
 *   first use.
 * - HelpButton: the header's entry point.
 */

import { Suspense, useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import {
  headerButtonClass,
  isTypingTarget,
  lazyPage,
  ShortcutsDialog,
  useModKey,
  useMountedOnce,
} from "@digitalisation/enlixy-comp";
import { TourProvider, useLearningMilestones } from "@digitalisation/enlixy-comp/onboarding";
import { useAssistantUi } from "../components/cockpit/assistant/assistantUiStore";
import { useSAPDataStore } from "../store/SAPDataStore";
import { useDashboardTabsStore } from "../store/DashboardTabsStore";
import { buildTasks, buildTours, chartKeys, type CockpitTour, type CockpitTourStep } from "./content";
import { useHelpUi } from "./helpUiStore";
import { learning, track } from "./learning";
import { machineForTour } from "./machineForTour";

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

/** The guided tours; above everything that starts one (useTour). */
export function CockpitTourProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // read in the async prepare: the path of the moment, not of the render
  const path = useRef(pathname);
  path.current = pathname;
  const tours = useMemo(() => buildTours(t), [t]);
  // the drawer was opened by the tour: close it again when the tour ends
  const openedAssistant = useRef(false);

  const prepare = useCallback(
    async (step: CockpitTourStep) => {
      if (step.route === "home" && path.current !== "/") {
        navigate("/");
        await sleep(500);
      } else if (step.route === "machine" && !path.current.startsWith("/machine/")) {
        const id = machineForTour(path.current);
        if (id) {
          navigate(`/machine/${id}`);
          await sleep(900);
        }
      }
      // machines with live data open on Live Shift: these steps show the overview
      const machineId = /^\/machine\/([^/?#]+)/.exec(path.current)?.[1];
      if (step.tab && machineId) {
        const tabs = useDashboardTabsStore.getState();
        if (tabs.getMachineTabState(machineId)?.activeTabId !== step.tab) {
          tabs.setActiveTab(machineId, step.tab);
          await sleep(400);
        }
      }
      const assistant = useAssistantUi.getState();
      if (step.assistant === "open" && !assistant.isOpen) {
        assistant.open();
        openedAssistant.current = true;
        await sleep(400);
      } else if (step.assistant === "closed" && assistant.isOpen) {
        assistant.close();
        await sleep(250);
      }
    },
    [navigate],
  );

  return (
    <TourProvider<CockpitTourStep>
      tours={tours}
      prepare={prepare}
      beforeStart={() => {
        // dialogs, the date picker and the drawer would cover the tour
        const help = useHelpUi.getState();
        help.closeHelp();
        help.setWelcomeOpen(false);
        help.setShortcutsOpen(false);
        useSAPDataStore.getState().setDatePickerOpen(false);
        useAssistantUi.getState().close();
        openedAssistant.current = false;
      }}
      onFinish={(tour) => track((tour as CockpitTour).event)}
      onStop={() => {
        if (openedAssistant.current) useAssistantUi.getState().close();
        openedAssistant.current = false;
      }}
    >
      {children}
    </TourProvider>
  );
}

// loaded on first use, kept out of the main bundle
const HelpSheet = lazyPage(() => import("./HelpSheet").then((m) => ({ default: m.HelpSheet })));
const Welcome = lazyPage(() => import("./Welcome").then((m) => ({ default: m.Welcome })));

export function Onboarding() {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const sub = keycloak?.tokenParsed?.sub ?? null;
  const helpOpen = useHelpUi((s) => s.open);
  const welcomeOpen = useHelpUi((s) => s.welcomeOpen);
  const shortcutsOpen = useHelpUi((s) => s.shortcutsOpen);
  const openHelp = useHelpUi((s) => s.openHelp);
  const setWelcomeOpen = useHelpUi((s) => s.setWelcomeOpen);

  // progress per user; the welcome once, on the first visit (not in
  // automated browsers: the video captures and browser tests)
  useEffect(() => {
    if (!sub) return;
    learning.setUser(sub);
    if (learning.has("intro_seen") || navigator.webdriver) return;
    const timer = window.setTimeout(() => setWelcomeOpen(true), 900);
    return () => window.clearTimeout(timer);
  }, [sub, setWelcomeOpen]);

  const tasks = useMemo(() => buildTasks(t), [t]);
  useLearningMilestones(learning, tasks, {
    // these complete while a dialog is open, a toast would sit under it
    ignore: ["intro_seen", "help_read"],
    onOpenPath: () => openHelp("path"),
  });

  // F1 opens the help from anywhere, "?" when not typing
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "F1") {
        e.preventDefault();
        openHelp();
      } else if (e.key === "?" && !e.ctrlKey && !e.metaKey && !e.altKey && !isTypingTarget(e.target)) {
        e.preventDefault();
        openHelp();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openHelp]);

  const help = useMountedOnce(helpOpen);
  const welcome = useMountedOnce(welcomeOpen);
  const shortcuts = useMountedOnce(shortcutsOpen);
  return (
    <>
      <Suspense fallback={null}>
        {help && <HelpSheet />}
        {welcome && <Welcome />}
      </Suspense>
      {shortcuts && <CockpitShortcuts />}
    </>
  );
}

/** "Keyboard shortcuts" of the help sheet's footer. */
function CockpitShortcuts() {
  const { t } = useTranslation();
  const open = useHelpUi((s) => s.shortcutsOpen);
  const setOpen = useHelpUi((s) => s.setShortcutsOpen);
  const s = (key: string) => t(`helpCenter.shortcuts.${key}`);
  const mod = useModKey();
  return (
    <ShortcutsDialog
      open={open}
      onClose={() => setOpen(false)}
      groups={[
        {
          title: s("groupApp"),
          items: [
            [[mod, "K"], s("search")],
            [["F1"], s("help")],
            [["?"], s("helpNotTyping")],
            [["Esc"], s("close")],
            [["→"], s("tourNext")],
            [["←"], s("tourBack")],
          ],
        },
        { title: s("groupCharts"), items: chartKeys(t) },
        {
          title: s("groupAssistant"),
          items: [
            [["Enter"], s("send")],
            [["Shift", "Enter"], s("newline")],
          ],
        },
      ]}
    />
  );
}

/**
 * The header's "Help and tours" button: round like the language button and the
 * avatar next to it, with a bold "?" (the package's circled question mark drew
 * a ring inside the button's ring).
 */
export function HelpButton() {
  const { t } = useTranslation();
  const openHelp = useHelpUi((s) => s.openHelp);
  const label = `${t("helpCenter.button")} (F1)`;
  return (
    <button
      type="button"
      onClick={() => openHelp()}
      aria-label={label}
      title={label}
      data-tour="help-button"
      className={headerButtonClass()}
    >
      <svg
        viewBox="0 0 24 24"
        width={18}
        height={18}
        fill="none"
        stroke="currentColor"
        strokeWidth={2.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M8.5 8.8a3.6 3.6 0 0 1 7 1.2c0 2.4-3.5 2.9-3.5 5.2" />
        <path d="M12 19.3h.01" />
      </svg>
    </button>
  );
}

/** /help (bookmarks of the former help page): the handbook over the start page. */
export function HelpRoute() {
  const navigate = useNavigate();
  const openHelp = useHelpUi((s) => s.openHelp);
  useEffect(() => {
    openHelp("handbook");
    navigate("/", { replace: true });
  }, [navigate, openHelp]);
  return null;
}
